import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './button';
import { Input } from './InputDefault';
import { Label } from './label';
import { Badge } from './badge';
import { TagItem, createTag } from '@/lib/stores/tagsStore';
import { useToast } from '@/hooks/use-toast';
import IconProvider from './IconProvider';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TagSelectorProps {
  selectedTags: string[]; // Now represents tag IDs instead of tag names
  onTagsChange: (tags: string[]) => void; // Now receives tag IDs instead of tag names
  availableTags: TagItem[];
  isLoading?: boolean;
  onRefreshTags?: () => void;
  placeholder?: string;
  className?: string;
  hideLabel?: boolean; // Hide the "Tags" label
  disableCreate?: boolean; // Disable tag creation functionality
}

export default function TagSelector({
  selectedTags,
  onTagsChange,
  availableTags,
  isLoading = false,
  onRefreshTags,
  placeholder = "Select or add tags...",
  className = "",
  hideLabel = false,
  disableCreate = false,
}: TagSelectorProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagGroup, setNewTagGroup] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter available tags based on search term
  const filteredTags = availableTags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tag.group_name && tag.group_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  // Filter out already selected tags (by ID)
  const unselectedTags = filteredTags.filter(tag => !selectedTags.includes(tag.id));
  // Show create new tag option if search term doesn't match any existing tags
  const shouldShowCreateOption = !disableCreate && searchTerm.trim() && 
    !availableTags.some(tag => tag.name.toLowerCase() === searchTerm.toLowerCase()) &&
    !availableTags.some(tag => selectedTags.includes(tag.id) && tag.name.toLowerCase() === searchTerm.toLowerCase());

  const handleTagSelect = (tagId: string) => {
    if (!selectedTags.includes(tagId)) {
      onTagsChange([...selectedTags, tagId]);
    }
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleTagRemove = (tagId: string) => {
    onTagsChange(selectedTags.filter(tag => tag !== tagId));
  };

  const handleCreateTag = async () => {
    if (!selectedGame || !newTagName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a tag name',
        variant: 'destructive',
      });
      return;
    }

    // Check if tag already exists
    if (availableTags.some(tag => tag.name.toLowerCase() === newTagName.trim().toLowerCase())) {
      toast({
        title: 'Error',
        description: 'A tag with this name already exists',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingTag(true);
    try {
      const tagNameToCreate = shouldShowCreateOption ? searchTerm.trim() : newTagName.trim();
      
      const newTag = await createTag({
        game_id: selectedGame.id,
        name: tagNameToCreate,
        group: newTagGroup.trim() || undefined,
        color: newTagColor,
      }, toast);

      // Add the new tag ID to selected tags
      onTagsChange([...selectedTags, newTag.id]);
      
      // Reset form
      setNewTagName('');
      setNewTagGroup('');
      setNewTagColor('#6366f1');
      setSearchTerm('');
      
      // Refresh tags list
      if (onRefreshTags) {
        onRefreshTags();
      }

      toast({
        title: 'Success',
        description: 'Tag created successfully',
      });
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setIsCreatingTag(false);
    }
  };

  const getTagColor = (tagId: string) => {
    console.log("getTagColor", tagId);
    const tag = availableTags.find(t => t.id === tagId);
    console.log("getTagColor", tag);
    return tag?.color || '#6366f1';
  };

  const getTagName = (tagId: string) => {
    const tag = availableTags.find(t => t.id === tagId);
    return tag?.name || 'Unknown Tag';
  };

  const getTagGroup = (tagId: string) => {
    const tag = availableTags.find(t => t.id === tagId);
    return tag?.group_name;
  };

  return (
    <div className={`relative ${className}`}>
      {!hideLabel && (
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          Tags
        </Label>
      )}
      
      {/* Selected tags display */}
      <div className="flex flex-wrap gap-2 mb-3">
        {selectedTags.map(tagId => (
          <Badge
            key={tagId}
            variant="secondary"
            className="flex items-center gap-1 px-2 py-1"
            style={{ backgroundColor: getTagColor(tagId) + '20', color: getTagColor(tagId) }}
          >
            <span className="text-xs">{getTagName(tagId)}</span>
            {getTagGroup(tagId) && (
              <span className="text-xs opacity-70">({getTagGroup(tagId)})</span>
            )}
            <button
              onClick={() => handleTagRemove(tagId)}
              className="ml-1 hover:bg-black/10 rounded-full p-0.5"
            >
              <IconProvider iconstring="fa-times" size={10} color="currentColor" />
            </button>
          </Badge>
        ))}
      </div>

      {/* Tag selector input */}
      <div className="relative">
        <Input
          ref={inputRef}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && shouldShowCreateOption && searchTerm.trim() && !disableCreate) {
              e.preventDefault();
              handleCreateTag();
            }
          }}
          placeholder={placeholder}
          className="w-full"
        />
        
        {/* Portal for dropdown */}
        {isOpen && typeof document !== 'undefined' && createPortal(
          <div 
            ref={dropdownRef}
            className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {/* Available tags */}
            {unselectedTags.length > 0 && (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 mb-2 px-2">{t('platform.ui.tagselector.available_tags')}</div>
                {unselectedTags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => handleTagSelect(tag.id)}
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: tag.color || '#6366f1' }}
                    />
                    <span className="text-sm">{tag.name}</span>
                    {tag.group_name && (
                      <span className="text-xs text-gray-500">({tag.group_name})</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Create new tag section */}
            {!disableCreate && (
              <div className="border-t border-gray-200 p-3">
                <div className="text-xs font-medium text-gray-500 mb-2">
                  {shouldShowCreateOption ? `Create "${searchTerm}"` : 'Create New Tag'}
                </div>
                
                <div className="space-y-2">
                  <Input
                    value={shouldShowCreateOption ? searchTerm : newTagName}
                    onChange={(e) => {
                      if (shouldShowCreateOption) {
                        setSearchTerm(e.target.value);
                      } else {
                        setNewTagName(e.target.value);
                      }
                    }}
                    placeholder={t('platform.ui.tagselector.tag_name')}
                    className="text-sm"
                  />
                  
                  <Input
                    value={newTagGroup}
                    onChange={(e) => setNewTagGroup(e.target.value)}
                    placeholder={t('platform.ui.tagselector.group_optional')}
                    className="text-sm"
                  />
                  
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-600">{t('platform.ui.tagselector.color')}</Label>
                    <input
                      type="color"
                      value={newTagColor}
                      onChange={(e) => setNewTagColor(e.target.value)}
                      className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                    />
                  </div>
                  
                  <Button
                    onClick={handleCreateTag}
                    disabled={isCreatingTag || !(shouldShowCreateOption ? searchTerm.trim() : newTagName.trim())}
                    size="sm"
                    className="w-full"
                  >
                    {isCreatingTag ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b border-white mr-2" />
                        Creating...
                      </>
                    ) : (
                      'Create Tag'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
} 